import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { UserBadge } from '../components/auth/UserBadge'
import { StratIQXHeader } from '../components/ui/StratIQXHeader'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { SearchBar } from '../components/support/SearchBar'
import { FAQSection } from '../components/support/FAQSection'
import { ContactForm } from '../components/support/ContactForm'
import { useAuthStore } from '../stores/authStore'
import { SUPPORT_PAGE_DATA } from '../data/faq.data'
import { HelpCircle, Search, MessageCircle, BookOpen } from 'lucide-react'

export const SupportPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')


  const supportFeatures = [
    {
      icon: <Search className="h-8 w-8 text-blue-600" />,
      title: 'Smart Search',
      description: 'Find answers quickly with our intelligent search that understands your questions and suggests relevant topics.'
    },
    {
      icon: <BookOpen className="h-8 w-8 text-green-600" />,
      title: 'Comprehensive FAQs',
      description: 'Detailed answers covering every aspect of the onboarding process, from setup to advanced features.'
    },
    {
      icon: <MessageCircle className="h-8 w-8 text-purple-600" />,
      title: 'Direct Contact',
      description: 'Can\'t find what you need? Send us a message and get personalized help from our support team.'
    },
    {
      icon: <HelpCircle className="h-8 w-8 text-orange-600" />,
      title: 'Step-by-Step Guidance',
      description: 'Get detailed explanations for each step of your StratIQX intelligence setup journey.'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Add unified navigation */}
      <MainNavigation variant="unified" />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <StratIQXHeader
            subtitle="Help Center"
            title="Support & Frequently Asked Questions"
            description="Get help with your StratIQX intelligence platform setup and find answers to common questions"
          />
        </div>

        {/* Support Features Overview */}
        <div className="mb-12">
          <Card className="shadow-lg border-0 bg-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                How We Can Help You
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {supportFeatures.map((feature, index) => (
                  <div key={index} className="text-center">
                    <div className="flex justify-center mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="mb-12">
          <Card className="shadow-lg border-0 bg-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Find Your Answer
              </h2>
              <SearchBar 
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                faqs={SUPPORT_PAGE_DATA.faqs}
                className="max-w-2xl mx-auto"
              />
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <Card className="shadow-lg border-0 bg-white">
            <CardContent className="p-8">
              <FAQSection
                faqs={SUPPORT_PAGE_DATA.faqs}
                categories={SUPPORT_PAGE_DATA.categories}
                searchQuery={searchQuery}
              />
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <div className="text-center">
          <Card className="shadow-xl border-0 bg-white max-w-2xl mx-auto">
            <CardContent className="p-8">
              <ContactForm
                subjects={SUPPORT_PAGE_DATA.contactSubjects}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <MainFooter variant="minimal" />
    </div>
  )
}